import type { BigNumber, BigNumberish } from 'ethers';
import React from 'react';

import type { StarsStaking } from '../lib/contracts';
import { StarsStakingFactory } from '../lib/contracts';
import { STARS_STAKING_Address } from '../lib/smart-contract-utils';
import { useContract } from './contract.hooks';
import { useWeb3State } from './use-web3-state';

export const useStarStakingContract = (hardOverrideProvider = false) => {
  const { walletDetails } = useWeb3State();
  const { contract, connect } = useContract<StarsStaking>(
    StarsStakingFactory,
    STARS_STAKING_Address,
    STARS_STAKING_Address[walletDetails.chain.type],
    walletDetails.chain.type,
    hardOverrideProvider,
  );

  const getPool = async (index: BigNumberish) => {
    const pool = await contract?.poolInfo(index);

    return { pool };
  };

  const getPoolLength = async (): Promise<{
    poolLength: number;
  }> => {
    let poolLength: BigNumber | undefined;
    try {
      poolLength = await contract?.poolLength();
    } catch {
      poolLength = undefined;
    }

    return { poolLength: poolLength?.toNumber() ?? 0 };
  };

  const getDeposit = async (
    pid: number,
    user: string,
    depositId: number,
  ): Promise<{
    deposit: StarsStaking.DepositStructOutput | undefined;
  }> => {
    const deposit = await contract?.getDeposit(pid, user, depositId);

    return { deposit };
  };

  const addPrizePool = async (pid: number, prizePool: BigNumber) => {
    try {
      await contract?.addPrizePool(pid, prizePool);
      return { success: true };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'addPrizePool',
        },
        contexts: {},
      });
      return { success: false };
    }
  };

  const addPool = async (
    lpToken: string,
    minLockupTime: BigNumber,
    prizePool: BigNumber,
    startBlock: BigNumber,
    endBlock: BigNumber,
  ) => {
    try {
      await contract?.addPool(
        lpToken,
        minLockupTime,
        prizePool,
        startBlock,
        endBlock,
      );
      return { success: true };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'addPool',
        },
        contexts: {},
      });
      return { success: false };
    }
  };

  const massUpdatePools = async (pids: number[]) => {
    try {
      await contract?.massUpdatePools(pids);
      return { success: true };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'massUpdatePools',
        },
        contexts: {},
      });
      return { success: false };
    }
  };

  const updatePool = async (pid: number) => {
    try {
      const pool = await contract?.updatePool(pid);
      return { pool };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'updatePool',
        },
        contexts: {},
      });
      return { pool: undefined };
    }
  };

  const pendingSTARS = async (pid: number, user: string, depositId: number) => {
    try {
      const pendingStars = await contract?.pendingMogul(pid, user, depositId);
      return { pendingStars };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'pendingSTARS',
        },
        contexts: {},
      });
      return { pendingStars: undefined };
    }
  };

  const deposit = async (pid: number, amount: BigNumber, to: string) => {
    try {
      const txn = await contract?.deposit(pid, amount, to);
      if (txn === undefined) {
        throw new Error('missing transaction');
      }
      const txnReceipt = await txn.wait();
      return { success: true, transactionHash: txnReceipt.transactionHash };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'deposit',
        },
        contexts: {},
      });
      return { success: false, transactionHash: '' };
    }
  };

  const withdraw = async (pid: number, to: string, depositId: number) => {
    try {
      const txn = await contract?.withdraw(pid, to, depositId);
      if (txn === undefined) {
        throw new Error('missing transaction');
      }
      const txnReceipt = await txn.wait();
      return { success: true, transactionHash: txnReceipt.transactionHash };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'withdraw',
        },
        contexts: {},
      });
      return { success: false, transactionHash: '' };
    }
  };

  const harvest = async (pid: number, to: string, depositId: number) => {
    try {
      const txn = await contract?.harvest(pid, to, depositId);
      if (txn === undefined) {
        throw new Error('missing transaction');
      }
      const txnReceipt = await txn.wait();
      return { success: true, transactionHash: txnReceipt.transactionHash };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'harvest',
        },
        contexts: {},
      });
      return { success: false, transactionHash: '' };
    }
  };

  const withdrawAndHarvest = async (
    pid: number,
    to: string,
    depositId: number,
  ) => {
    try {
      const txn = await contract?.withdrawAndHarvest(pid, to, depositId);
      if (txn === undefined) {
        throw new Error('missing transaction');
      }
      const txnReceipt = await txn.wait();
      return { success: true, transactionHash: txnReceipt.transactionHash };
    } catch (e) {
      console.error(e);
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-staking.hook.ts',
          function: 'withdrawAndHarvest',
        },
        contexts: {},
      });
      return { success: false, transactionHash: '' };
    }
  };

  const depositLength = async (pid: number, userAddress: string) => {
    try {
      const length = await contract?.depositLength(pid, userAddress);

      return { length };
    } catch (error) {
      window.Sentry?.captureException(error, {
        tags: { file: 'stars-staking.hook.ts', function: 'depositLength' },
        contexts: {},
      });

      return { length: undefined };
    }
  };

  const getLockupRemaining = async (
    pid: number,
    userAddress: string,
    depositId: number,
  ) => {
    try {
      const blocksLeft = await contract?.lockupRemaining(
        pid,
        userAddress,
        depositId,
      );

      return { blocksLeft };
    } catch (error) {
      window.Sentry?.captureException(error, {
        tags: { file: 'stars-staking.hook.ts', function: 'getLockupRemaining' },
        contexts: {},
      });

      return { blocksLeft: undefined };
    }
  };

  return {
    contractInitialized: contract !== undefined,
    walletDetails,
    connect,
    getPool,
    getPoolLength,
    getDeposit,
    // setRewarder,
    // setPoolEndBlock,
    addPrizePool,
    addPool,
    massUpdatePools,
    updatePool,
    pendingSTARS,
    deposit,
    withdraw,
    harvest,
    // harvestAll,
    withdrawAndHarvest,
    depositLength,
    getLockupRemaining,
  };
};
